/**
 * Forced password change. Shown when profiles.password_reset_required is true (an
 * admin set a temporary password). AuthContext's RequireAuth makes this the only
 * allowed authenticated route until the user sets a new password. On success we
 * clear the flag server-side, then force a full reload so the app re-reads a fresh
 * profile (flag now false) before continuing.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../services/supabase";
import { useAuth } from "../contexts/AuthContext";

const MIN_LEN = 8;

export default function ForcePasswordChange() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (pw.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (pw !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({
        password: pw,
      });
      if (updErr) {
        throw updErr;
      }
      // Clear the force-change flag (scoped to the caller's own row server-side).
      const { error: clearErr } = await supabase.rpc(
        "clear_password_reset_required",
      );
      if (clearErr) {
        // Password changed but flag not cleared — surface so the user can retry the
        // clear rather than getting stuck behind the gate.
        throw new Error(
          "Your password was updated, but we couldn't finish. Please try again.",
        );
      }
      // Refresh the profile so the gate sees password_reset_required=false. refreshUser
      // updates BOTH in-memory state AND the localStorage cache, so RequireAuth won't
      // bounce us back here (a stale cache was the bug). Then continue into the app.
      await refreshUser();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not update password.",
      );
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
        <h1 className="text-2xl font-bold text-gray-900">Set a new password</h1>
        <p className="mt-2 text-sm text-gray-600">
          An administrator reset your password. For your security, please choose
          a new password before continuing
          {user?.email ? ` as ${user.email}` : ""}.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="new-password"
            >
              New password
            </label>
            <div className="relative">
              <input
                id="new-password"
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoComplete="new-password"
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 pr-16 text-base"
                required
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 px-3 text-sm font-medium text-blue-600"
              >
                {show ? "Hide" : "Show"}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              At least {MIN_LEN} characters.
            </p>
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="confirm-password"
            >
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base"
              required
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white disabled:opacity-50"
          >
            {busy ? "Updating…" : "Set new password & continue"}
          </button>
        </form>

        {/*
          Awaited and disabled while it runs. Sign-out now waits on the network (up to 4s)
          before clearing locally, so an unawaited click looked like a dead button on a slow
          connection. It also navigates explicitly rather than relying on the route guard —
          signOutCompletely() requires a navigation afterwards, because on the timeout path
          the token is gone from storage while auth-js may still hold the session in memory.
        */}
        <button
          type="button"
          disabled={signingOut}
          onClick={async () => {
            setSigningOut(true);
            try {
              await logout();
            } finally {
              window.location.href = "/login";
            }
          }}
          className="mt-4 w-full text-center text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-60"
        >
          {signingOut ? "Signing out…" : "Sign out instead"}
        </button>
      </div>
    </div>
  );
}
